import { Activity } from 'iconsax-reactjs';
import { WidgetCard } from '@/components/WidgetCard';
import { cn } from '@/lib/utils';
import { useSymbol } from '@/lib/useSymbol';
import { rsiTotals, useRsiSummary } from './api';
import { RsiGauge } from './RsiGauge';
import { useI18n, type TranslationKey } from '@/lib/i18n';

export type RsiBias = 'bullish' | 'bearish' | 'neutral';

/**
 * Net read across the seven timeframes, by simple majority of their verdicts.
 *
 * Unweighted on purpose. Weighting by timeframe would be defensible — a daily
 * signal usually outranks a 1-minute one — but the right weights depend on the
 * holding period being traded, and inventing them here would bury a trading
 * assumption inside a dashboard tile.
 */
export function rsiBias(bullish: number, bearish: number): RsiBias {
  if (bullish > bearish) return 'bullish';
  if (bearish > bullish) return 'bearish';
  return 'neutral';
}

const BIAS_STYLE: Record<RsiBias, { key: TranslationKey; className: string }> = {
  bullish: { key: 'common.bullish', className: 'bg-bull/15 text-bull' },
  bearish: { key: 'common.bearish', className: 'bg-bear/15 text-bear' },
  neutral: { key: 'common.neutral', className: 'bg-muted text-muted-foreground' },
};

/**
 * RSI across every timeframe, one gauge per row.
 *
 * A single net verdict was hiding the thing that actually matters here — whether
 * the timeframes agree. Seven rows of readings show alignment or conflict
 * immediately, which one badge never could.
 */
export function RsiWidget() {
  const { symbol, isLoading: symbolLoading } = useSymbol();
  const summary = useRsiSummary(symbol);
  const { t } = useI18n();

  const rows = summary.data?.timeframes ?? [];
  const totals = rsiTotals(rows);
  const bias = rsiBias(totals.bullish, totals.bearish);
  const style = BIAS_STYLE[bias];
  const loading = summary.isPending || symbolLoading;

  return (
    <WidgetCard
      to="/rsi"
      icon={<Activity size={16} variant="Bold" />}
      title={t('rsi.title')}
      module="rsi"
      badge={
          loading ? (
            <span className="h-4 w-14 animate-pulse rounded bg-muted" />
          ) : (
            <span className={cn('rounded px-1.5 py-0.5 text-[11px] font-semibold', style.className)}>
              {t(style.key)}
            </span>
          )
      }
    >

      <div className="flex h-full min-h-0 flex-col gap-1.5 p-2">
        {loading ? (
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-x-2 gap-y-1 [grid-auto-rows:minmax(0,1fr)]">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="min-h-0 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : (
          /*
           * Two per row, with the rows sharing the leftover height equally and
           * each dial scaling to its own cell.
           *
           * Sized by HEIGHT rather than width on purpose: there are eight
           * timeframes, the card is a third of the board on a desktop and the
           * full width on a phone, and anything width-driven either overflows
           * into a scrollbar at one size or leaves the dials floating at the
           * other. This way all eight are always visible.
           */
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-x-2 gap-y-1 [grid-auto-rows:minmax(0,1fr)]">
            {rows.map((r) => (
              <RsiGauge key={r.timeframe} row={r} />
            ))}
          </div>
        )}

        <p className="shrink-0 text-[10px] leading-tight text-muted-foreground">
          {symbol ?? '—'} ·{' '}
          {t('rsi.widgetSummary', {
            count: totals.divergences,
            bullish: totals.bullish,
            bearish: totals.bearish,
          })}
        </p>
      </div>
    </WidgetCard>
  );
}
